/**
 * Комбо-графіки розділу «Фінансовий результат» на дизайн-токенах (палітра —
 * колірна догма, світла/темна). Два експорти:
 *   PlannedIncomeChart — бари відпуску (МВт·год) + лінія планового доходу (грн).
 *   ImbalanceChart     — бари небалансу pos↑/neg↓ (кВт·год) + лінія(ї) вартості.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, Cell, LabelList, Rectangle, XAxis, YAxis,
  Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';
import { finChartColors, finAxisLabel, fmtMwh, fmtUah, fmtSignMwh } from './overviewTypes';
import type { PlannedIncomeRow, ImbalanceRow, FinGran } from './overviewTypes';

const UAH0 = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString((i18n.language === 'en' ? 'en-GB' : 'uk-UA')) + ' ' + i18n.t('ak_fin_uah'));
const priceModelLabel = (m: string | null) => (m === 'fixed' ? i18n.t('ak_fin_fixed') : m === 'formula' ? i18n.t('ak_fin_formula') : '—');
// Компактний підпис точки даних (тис. → «к»), щоб не захаращувати вісь.
const COMPACT = (v: any) => (v == null ? '' : Math.abs(Number(v)) >= 1000 ? Math.round(Number(v) / 1000) + i18n.t('ak_fin_k') : String(Math.round(Number(v))));

// Бар зі скругленням за знаком: заокруглюється саме КІНЕЦЬ стовпчика — верх для
// додатних (вгору), НИЗ для від'ємних (нижче осі X). recharts для від'ємних барів
// віддає НЕГАТИВНУ height (y — при значенні, знизу), через що radius перевертався.
// Нормалізуємо прямокутник (height додатна, y — верхній), тоді radius буквальний:
// [tl,tr,br,bl]. Fill/stroke/opacity беруться з props (Cell + Bar) через spread.
function SignRoundedBar(props: any) {
  const { y, height } = props;
  const v = Number(props?.payload?.imb ?? props?.value ?? 0);
  const top = Math.min(y, y + height);
  const h = Math.abs(height);
  const r = Math.min(3, h);
  return <Rectangle {...props} y={top} height={h} radius={v >= 0 ? [r, r, 0, 0] : [0, 0, r, r]} />;
}

// ── Плановий дохід ────────────────────────────────────────────────────────
export function PlannedIncomeChart({ rows, gran, height = 320, compact = false, onActiveKeyChange }: { rows: PlannedIncomeRow[]; gran: FinGran; height?: number | string; compact?: boolean; onActiveKeyChange?: (key: string | number | null) => void }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const C = finChartColors(isDark);
  const data = rows.map((r) => ({
    x: finAxisLabel(r.key, gran),
    release: r.releaseMwh ?? 0,
    income: r.incomeUah ?? null,
    price: r.priceUahMwh,
    model: r.priceModel,
  }));

  const Tip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: C.tipBg, border: `1px solid ${C.tipLine}`, borderRadius: 10, padding: '9px 11px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
        <div style={{ color: C.tipText, fontWeight: 700, marginBottom: 5, fontFamily: 'Urbanist, Inter, sans-serif' }}>{gran === 'hour' ? t('ak_fin_hour', { label }) : label}</div>
        <div style={{ color: C.release }}>{t('ak_fin_mms_release_mwh', { v: fmtMwh(d.release) })}</div>
        <div style={{ color: C.tipText }}>{t('ak_fin_price_uah_mwh_2', { v: d.price == null ? '—' : Math.round(d.price).toLocaleString((i18n.language === 'en' ? 'en-GB' : 'uk-UA')) })}</div>
        {d.model && <div style={{ color: C.axis }}>{t('ak_fin_price_type', { v: priceModelLabel(d.model) })}</div>}
        <div style={{ color: C.tipText, marginTop: 3 }}>{t('ak_fin_planned_income')} ({t('ak_fin_without_vat')}): <b style={{ fontWeight: 700 }}>{UAH0(d.income)}</b></div>
      </div>
    );
  };

  const reportActive = (st: any) => {
    if (!onActiveKeyChange) return;
    const i = Number(st?.activeTooltipIndex ?? st?.activeIndex);
    onActiveKeyChange(Number.isInteger(i) && rows[i] ? rows[i].key : null);
  };

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: compact ? 6 : 18, right: compact ? 6 : 12, left: compact ? 0 : 8, bottom: 0 }} barCategoryGap="14%"
          onMouseMove={reportActive} onMouseLeave={() => onActiveKeyChange?.(null)}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="x" tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} interval={gran === 'hour' ? 0 : 'preserveStartEnd'} minTickGap={gran === 'hour' ? 0 : 14} />
          <YAxis yAxisId="mwh" tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} width={compact ? 34 : 56}
            label={compact ? undefined : { value: t('ak_fin_release_mwh'), angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: C.axis, textAnchor: 'middle' } }} />
          <YAxis yAxisId="uah" orientation="right" tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} width={compact ? 40 : 62} tickFormatter={(v) => (v >= 1000 ? Math.round(v / 1000) + i18n.t('ak_fin_k') : String(Math.round(v)))}
            label={compact ? undefined : { value: t('ak_fin_income_uah'), angle: -90, position: 'insideRight', style: { fontSize: 10, fill: C.axis, textAnchor: 'middle' } }} />
          <Tooltip content={<Tip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(40,50,60,0.05)' }} />
          {/* Широкий напівпрозорий бар із суцільним контуром і заокругленим верхом (стиль forecast-mms). */}
          <Bar yAxisId="mwh" dataKey="release" name={t('ak_fin_mms_release')} fill={C.release} fillOpacity={0.4} stroke={C.release} strokeWidth={1.4} radius={[3, 3, 0, 0]} maxBarSize={gran === 'hour' ? 34 : 24} isAnimationActive={false} />
          <Line yAxisId="uah" type="monotone" dataKey="income" name={`${t('ak_fin_planned_income')} (${t('ak_fin_without_vat')})`} stroke={C.income} strokeWidth={2} dot={false} connectNulls isAnimationActive={false}>
            {!compact && <LabelList dataKey="income" position="top" formatter={COMPACT} style={{ fontSize: 9, fontWeight: 600, fill: C.income, fontFamily: 'Urbanist, Inter, sans-serif' }} />}
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Небаланси ─────────────────────────────────────────────────────────────
export function ImbalanceChart({ rows, gran, showActual, height = 320, compact = false, onActiveKeyChange }: { rows: ImbalanceRow[]; gran: FinGran; showActual: boolean; height?: number | string; compact?: boolean; onActiveKeyChange?: (key: string | number | null) => void }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const C = finChartColors(isDark);
  const data = rows.map((r) => ({
    x: finAxisLabel(r.key, gran),
    imb: r.imbalanceKwh ?? 0,
    imbMwh: r.imbalanceMwh,
    current: r.currentCostUah ?? null,
    actual: r.actualCostUah ?? null,
  }));

  const Tip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: C.tipBg, border: `1px solid ${C.tipLine}`, borderRadius: 10, padding: '9px 11px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
        <div style={{ color: C.tipText, fontWeight: 700, marginBottom: 5, fontFamily: 'Urbanist, Inter, sans-serif' }}>{gran === 'hour' ? t('ak_fin_hour', { label }) : label}</div>
        <div style={{ color: (d.imb ?? 0) >= 0 ? C.imbPos : C.imbNeg }}>{t('ak_fin_imbalance_mwh', { v: fmtSignMwh(d.imbMwh) })}</div>
        <div style={{ color: C.tipText }}>{t('ak_fin_current_cost', { v: UAH0(d.current) })}</div>
        {showActual && <div style={{ color: C.axis }}>{t('ak_fin_actual_cost', { v: UAH0(d.actual) })}</div>}
        {showActual && d.current != null && d.actual != null && (
          <div style={{ color: C.tipText, fontWeight: 700, marginTop: 3 }}>{t('ak_fin_difference', { v: UAH0(d.current - d.actual) })}</div>
        )}
      </div>
    );
  };

  const reportActive = (st: any) => {
    if (!onActiveKeyChange) return;
    const i = Number(st?.activeTooltipIndex ?? st?.activeIndex);
    onActiveKeyChange(Number.isInteger(i) && rows[i] ? rows[i].key : null);
  };

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: compact ? 6 : 18, right: compact ? 6 : 12, left: compact ? 0 : 8, bottom: 0 }} barCategoryGap="14%"
          onMouseMove={reportActive} onMouseLeave={() => onActiveKeyChange?.(null)}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
          <XAxis dataKey="x" tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} interval={gran === 'hour' ? 0 : 'preserveStartEnd'} minTickGap={gran === 'hour' ? 0 : 14} />
          <YAxis yAxisId="kwh" tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} width={compact ? 36 : 58}
            label={compact ? undefined : { value: t('ak_fin_imbalance_kwh'), angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: C.axis, textAnchor: 'middle' } }} />
          <YAxis yAxisId="uah" orientation="right" tick={{ fontSize: 10, fill: C.axis }} axisLine={false} tickLine={false} width={compact ? 40 : 62} tickFormatter={(v) => (Math.abs(v) >= 1000 ? Math.round(v / 1000) + i18n.t('ak_fin_k') : String(Math.round(v)))}
            label={compact ? undefined : { value: t('ak_fin_cost_uah'), angle: -90, position: 'insideRight', style: { fontSize: 10, fill: C.axis, textAnchor: 'middle' } }} />
          <Tooltip content={<Tip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(40,50,60,0.05)' }} />
          <ReferenceLine yAxisId="kwh" y={0} stroke={C.axis} strokeOpacity={0.5} />
          {/* Широкий напівпрозорий бар із суцільним контуром (стиль forecast-mms); колір per-sign. */}
          <Bar yAxisId="kwh" dataKey="imb" name={t('ak_fin_imbalance')} fillOpacity={0.4} strokeWidth={1.4} maxBarSize={gran === 'hour' ? 34 : 24} isAnimationActive={false} shape={<SignRoundedBar />}>
            {data.map((d, i) => { const c = (d.imb ?? 0) >= 0 ? C.imbPos : C.imbNeg; return <Cell key={i} fill={c} stroke={c} />; })}
          </Bar>
          <Line yAxisId="uah" type="monotone" dataKey="current" name={t('ak_fin_current_cost_2')} stroke={C.costCurrent} strokeWidth={2} dot={false} connectNulls isAnimationActive={false}>
            {!compact && <LabelList dataKey="current" position="top" formatter={COMPACT} style={{ fontSize: 9, fontWeight: 600, fill: C.costCurrent, fontFamily: 'Urbanist, Inter, sans-serif' }} />}
          </Line>
          {showActual && (
            <Line yAxisId="uah" type="monotone" dataKey="actual" name={t('ak_fin_actual_cost_2')} stroke={C.costActual} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
